import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../common/model/dto/order/order';
import {
  countProvidedMarketingValues,
  getOrderMarketingValues,
} from '../../../common/utils/services/order-marketing-utils';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvLabelledField from '../atoms/OvLabelledField';

const OvOrderMarketingSection: FC<{ order: Order }> = ({ order }) => {
  const { t } = useTranslation();
  const values = getOrderMarketingValues(order);
  const providedCount = countProvidedMarketingValues(values);
  const [isOpen, setIsOpen] = useState<boolean>(providedCount > 0);

  return (
    <Section>
      <details
        open={isOpen}
        onToggle={(event) => setIsOpen(event.currentTarget.open)}
      >
        <summary>
          <h2>{t('orders.details.marketing')}</h2>
          <span>
            {providedCount > 0
              ? t('orders.details.marketingProvided', {
                  count: providedCount,
                  total: values.length,
                })
              : t('orders.details.marketingEmpty')}
          </span>
        </summary>
        <Fields>
          {values.map(({ labelKey, value }) => (
            <OvLabelledField key={labelKey} label={t(labelKey)}>
              {value || t('orders.delivery.notProvided')}
            </OvLabelledField>
          ))}
        </Fields>
      </details>
    </Section>
  );
};

export default OvOrderMarketingSection;

const Section = styled.section`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  summary {
    cursor: pointer;
  }

  summary:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }

  h2 {
    display: inline;
    margin: 0;
    font-size: ${Variables.fontSizes.LARGE};
  }

  summary span {
    margin-left: 0.375rem;
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
  }

  @media (max-width: ${breakpoints.lg}) {
    padding: 1rem 1.25rem;
  }
`;

const Fields = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem 1.25rem;
  margin: 1.25rem 0 0.25rem;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
